import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@foxera/environments/enviroment';
import {
  IWorkoutInfoRequest,
  IWorkoutPlanResponse,
  IWorkoutPlanListItem,
  IWorkoutPlanUpdateRequest,
  IExerciseSearchResponse,
  IExerciseSearchParams,
  IExerciseFilterConstants,
  IWorkoutPlansListResponse,
  IWorkoutPlanJob,
  IGenerateWorkoutPlanResponse,
} from '@foxera/shared/interfaces/client.interface';
import {
  IWorkoutConstantsApiResponse,
} from '@foxera/feature/clients/interfaces/workout.constants.interface';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class WorkoutInfoService {
  private readonly FITNESS_URL = `${environment.api_fitness_base_url}`;

  constructor(private http: HttpClient) {}

  getConstants(): Observable<IWorkoutConstantsApiResponse> {
    return this.http.get<IWorkoutConstantsApiResponse>(`${this.FITNESS_URL}/clients-profile/constants`);
  }

  /**
   * Get pain suggestions from backend
   * @param pain The pain query string
   * @returns Observable of string array with "suggestedPains" property
   */
  getPainSuggestions(pain: string): Observable<string[]> {
    const params = new HttpParams().set('pain', pain);
    return this.http.get<{ suggestedPains: string[] }>(`${this.FITNESS_URL}/clients-profile/pains/ai-suggestions`, { params })
      .pipe(
        map(response => response.suggestedPains)
      );
  }

  /**
   * Get posture problem suggestions from backend
   * @param postureProblem The posture problem query string
   * @returns Observable of string array with "suggestedPostureProblems" property
   */
  getPostureProblemSuggestions(postureProblem: string): Observable<string[]> {
    const params = new HttpParams().set('postureProblem', postureProblem);
    return this.http.get<{ suggestedPostureProblems: string[] }>(`${this.FITNESS_URL}/clients-profile/posture-problems/ai-suggestions`, { params })
      .pipe(
        map(response => response.suggestedPostureProblems)
      );
  }

  /**
   * Get athlete sport suggestions from backend
   * @param sport The sport query string
   * @returns Observable of string array with "suggestedSports" property
   */
  getSportSuggestions(sport: string): Observable<string[]> {
    const params = new HttpParams().set('sport', sport);
    return this.http.get<{ suggestedSports: string[] }>(`${this.FITNESS_URL}/clients-profile/athlete-sports/ai-suggestions`, { params })
      .pipe(
        map(response => response.suggestedSports)
      );
  }

  /**
   * Get muscle suggestions from backend based on user's profile
   * @param pains Array of pain issues
   * @param postureProblems Array of posture problems
   * @returns Observable with suggested muscles and reasoning
   */
  getMuscleSuggestions(pains: string[], postureProblems: string[]): Observable<{ suggestedMuscles: string[], reasoning: string }> {
    let params = new HttpParams();
    
    if (pains && pains.length > 0) {
      params = params.set('pains', pains.join(','));
    }
    
    if (postureProblems && postureProblems.length > 0) {
      params = params.set('postureProblems', postureProblems.join(','));
    }
    
    // Temporarily disabled: /api/v1/clients-profile/muscles/ai-suggestions
    // return this.http.get<{ suggestedMuscles: string[], reasoning: string }>(`${this.FITNESS_URL}/clients-profile/muscles/ai-suggestions`, { params });
    return of({ suggestedMuscles: [], reasoning: '' });
  }

    /**
   * Save workout information for a client
   * @param clientId Client ID
   * @param workoutInfo Workout information data
   * @returns Observable of the saved workout info
   */
    saveWorkoutInfo(clientId: number, workoutInfo: IWorkoutInfoRequest): Observable<any> {
      return this.http.put(`${this.FITNESS_URL}/clients-profile/${clientId}`, workoutInfo);
    }

    /**
   * Get workout plan by ID
   * @param workoutPlanId The workout plan ID
   * @returns Observable of the workout plan response
   */
    getWorkoutPlanById(workoutPlanId: number): Observable<IWorkoutPlanResponse> {
      return this.http.get<IWorkoutPlanResponse>(`${this.FITNESS_URL}/workout-plans/${workoutPlanId}`);
    }

    /**
   * Generate workout plan for a client (async via WebSocket)
   * @param clientId Client ID
   * @param trainingType Training type (Standard or Athlete) - required
   * @returns Observable with jobId to track generation progress
   */
    generateWorkoutPlan(clientId: number, trainingType: string): Observable<IGenerateWorkoutPlanResponse> {
      return this.http.post<IGenerateWorkoutPlanResponse>(
        `${this.FITNESS_URL}/workout-plans/${clientId}`,
        { trainingType }
      );
    }

    /**
   * Get all workout plans for a client
   * @param clientId Client ID
   * @returns Observable of array of workout plan list items
   */
    getWorkoutPlansByClientId(clientId: number): Observable<IWorkoutPlanListItem[]> {
      // The API returns an array directly, not wrapped in a response object
      return this.http.get<IWorkoutPlanListItem[]>(`${this.FITNESS_URL}/clients-profile/${clientId}/workoutplans`);
    }

    /**
     * Update workout plan by ID
     * PUT /workout-plans/:id
     */
    updateWorkoutPlan(workoutPlanId: number, payload: IWorkoutPlanUpdateRequest): Observable<IWorkoutPlanResponse> {
      return this.http.put<IWorkoutPlanResponse>(`${this.FITNESS_URL}/workout-plans/${workoutPlanId}`, payload);
    }

    /**
     * Get exercise filter constants
     * GET /exercises/:branchId/constants
     * @param branchId Branch ID
     * @param phase Optional phase filter (WarmUp, ResistanceTraining, Cardio, CoolDown)
     * @returns Observable of filter constants
     */
    getExerciseFilterConstants(branchId: string, phase?: string): Observable<IExerciseFilterConstants> {
      let httpParams = new HttpParams();
      
      if (phase) {
        httpParams = httpParams.set('phase', phase);
      }
      
      return this.http.get<IExerciseFilterConstants>(`${this.FITNESS_URL}/exercises/${branchId}/constants`, { params: httpParams });
    }

    /**
     * Search exercises with pagination and filters
     * GET /exercises/:branchId
     * @param branchId Branch ID
     * @param params Search parameters (skip, top, name, levels, categories, muscles, equipment, phase)
     * @returns Observable of exercise search response
     */
    searchExercises(branchId: string, params: IExerciseSearchParams = {}): Observable<IExerciseSearchResponse> {
      let httpParams = new HttpParams();
      
      if (params.skip !== undefined) {
        httpParams = httpParams.set('skip', params.skip.toString());
      }
      if (params.top !== undefined) {
        httpParams = httpParams.set('top', params.top.toString());
      }
      if (params.name) {
        httpParams = httpParams.set('Name', params.name);
      }
      if (params.categories && params.categories.length > 0) {
        params.categories.forEach(cat => {
          httpParams = httpParams.append('categories', cat);
        });
      }
      if (params.muscles && params.muscles.length > 0) {
        params.muscles.forEach(muscle => {
          httpParams = httpParams.append('muscles', muscle);
        });
      }
      if (params.muscleFilterMode) {
        httpParams = httpParams.set('MuscleFilterMode', params.muscleFilterMode);
      }
      if (params.mainEquipment) {
        httpParams = httpParams.set('mainEquipment', params.mainEquipment);
      }
      if (params.supportEquipment) {
        httpParams = httpParams.set('supportEquipment', params.supportEquipment);
      }
      if (params.levels && params.levels.length > 0) {
        params.levels.forEach(level => {
          httpParams = httpParams.append('levels', level);
        });
      }
      if (params.phases && params.phases.length > 0) {
        params.phases.forEach(phase => {
          httpParams = httpParams.append('Phases', phase);
        });
      }
      if (params.clientId !== undefined) {
        httpParams = httpParams.set('clientId', params.clientId.toString());
      }
      if (params.exerciseAccess) {
        httpParams = httpParams.set('exerciseAccess', params.exerciseAccess);
      }
      if (params.movementPatterns && params.movementPatterns.length > 0) {
        params.movementPatterns.forEach(pattern => {
          httpParams = httpParams.append('MovementPatterns', pattern);
        });
      }
      
      return this.http.get<IExerciseSearchResponse>(`${this.FITNESS_URL}/exercises/${branchId}`, { params: httpParams });
    }

    /**
     * Get active workout plans with optional filters
     * GET /api/v1/workout-plans/active
     * @param top - Number of records to take
     * @param skip - Number of records to skip
     * @param isReviewed - Optional filter for reviewed status (true/false)
     * @returns Observable of workout plans list response
     */
    getActiveWorkoutPlans(top: number = 20, skip: number = 0, isReviewed?: boolean): Observable<IWorkoutPlansListResponse> {
      let params = new HttpParams()
        .set('top', top.toString())
        .set('skip', skip.toString());
      
      if (isReviewed !== undefined) {
        params = params.set('isReviewed', isReviewed.toString());
      }
      
      return this.http.get<IWorkoutPlansListResponse>(`${this.FITNESS_URL}/workout-plans/active`, { params });
    }

    /**
     * Get all workout plan jobs for an employee (last 24 hours)
     * GET /workout-plans/jobs/employee/:employeeId
     */
    getEmployeeJobs(employeeId: number): Observable<IWorkoutPlanJob[]> {
      return this.http.get<IWorkoutPlanJob[]>(
        `${this.FITNESS_URL}/workout-plans/jobs/employee/${employeeId}`
      );
    }

    /**
     * Cancel a workout plan job
     * PATCH /workout-plans/jobs/:jobId/cancel
     */
    cancelJob(jobId: string): Observable<void> {
      return this.http.patch<void>(
        `${this.FITNESS_URL}/workout-plans/jobs/${jobId}/cancel`,
        {}
      );
    }

    /**
     * Restart a failed workout plan job
     * PATCH /workout-plans/jobs/:jobId/restart
     */
    restartJob(jobId: string): Observable<void> {
      return this.http.patch<void>(
        `${this.FITNESS_URL}/workout-plans/jobs/${jobId}/restart`,
        {}
      );
    }
}





